import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Send,
  User,
  CheckCheck,
  Circle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/messages`, { headers });
      setMessages(response.data);
      
      // For admin, also fetch clients list
      if (user?.role === "admin") {
        const clientsRes = await axios.get(`${API}/admin/clients`, { headers });
        setClients(clientsRes.data);
      }
    } catch (err) {
      // Silent fail for background polling - avoid UI disruption
      console.debug('Background fetch error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user?.role]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, selectedConversation]);

  const getConversations = () => {
    const conversationMap = new Map();
    
    messages.forEach(msg => {
      const otherId = msg.sender_id === user.id ? msg.recipient_id : msg.sender_id;
      const otherName = msg.sender_id === user.id ? "Admin" : msg.sender_name;
      
      if (!conversationMap.has(otherId) || 
          new Date(msg.created_at) > new Date(conversationMap.get(otherId).lastMessage.created_at)) {
        conversationMap.set(otherId, {
          id: otherId,
          name: otherName,
          lastMessage: msg,
          unread: msg.recipient_id === user.id && !msg.is_read
        });
      }
    });
    
    return Array.from(conversationMap.values())
      .sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
  };

  const getConversationMessages = () => {
    if (!selectedConversation) return [];
    return messages
      .filter(msg => 
        (msg.sender_id === selectedConversation || msg.recipient_id === selectedConversation)
      )
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const markAsRead = async (messageId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/messages/${messageId}/read`, {}, { headers });
    } catch (err) {
      // Silent fail - marking as read is non-critical
      console.debug('Non-critical error marking message as read:', err?.message);
    }
  };

  const handleSelectConversation = async (conversationId) => {
    setSelectedConversation(conversationId);
    
    // Mark unread messages as read
    const unreadMessages = messages.filter(
      msg => msg.sender_id === conversationId && 
             msg.recipient_id === user.id && 
             !msg.is_read
    );
    
    for (const msg of unreadMessages) {
      await markAsRead(msg.id);
    }
    
    fetchMessages();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/messages`, {
        recipient_id: selectedConversation,
        content: newMessage.trim()
      }, { headers });
      
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const conversations = getConversations();
  const conversationMessages = getConversationMessages();

  // For clients, find admin to message
  const getAdminId = () => {
    // Find any message from admin or look in clients list
    const adminMessage = messages.find(m => m.sender_name === "Admin" || m.sender_id !== user.id);
    return adminMessage?.sender_id || "admin";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white" data-testid="messages-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center text-stone hover:text-olive transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <Link to="/" className="flex flex-col items-end">
            <span className="font-heading text-xl italic text-charcoal">The Pack Standard</span>
            <span className="text-olive text-xs tracking-wider">PUPPY ACADEMY</span>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="font-heading text-3xl text-charcoal mb-8">Messages</h1>

        <div className="grid md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <div className="card-premium p-0 overflow-hidden" data-testid="conversations-list">
            <div className="p-4 border-b border-olive/10">
              <h2 className="font-medium text-charcoal">Conversations</h2>
            </div>
            <ScrollArea className="h-[520px]">
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-stone text-sm">No messages yet</p>
                  {user?.role !== "admin" && (
                    <p className="text-stone text-xs mt-2">
                      Your messages with The Pack Standard will appear here.
                    </p>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-olive/10">
                  {conversations.map((conv) => (
                    <motion.button
                      key={conv.id}
                      whileHover={{ x: 4 }}
                      onClick={() => handleSelectConversation(conv.id)}
                      className={`w-full p-4 text-left transition-colors ${
                        selectedConversation === conv.id
                          ? "bg-olive/10"
                          : "hover:bg-sand/50"
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-olive/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-olive" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-charcoal truncate">{conv.name}</p>
                            {conv.unread && (
                              <Circle className="w-2 h-2 fill-terracotta text-terracotta" />
                            )}
                          </div>
                          <p className="text-sm text-stone truncate">
                            {conv.lastMessage.content}
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Messages Area */}
          <div className="md:col-span-2 card-premium p-0 flex flex-col overflow-hidden" data-testid="messages-area">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-olive/10">
                  <h2 className="font-medium text-charcoal">
                    {conversations.find(c => c.id === selectedConversation)?.name || "Conversation"}
                  </h2>
                </div>
                
                <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                  <div className="space-y-4">
                    {conversationMessages.map((msg) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.sender_id === user.id ? "justify-end" : "justify-start"}`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                            msg.sender_id === user.id
                              ? "bg-olive text-white rounded-br-md"
                              : "bg-sand text-charcoal rounded-bl-md"
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            msg.sender_id === user.id ? "text-white/60" : "text-stone"
                          }`}>
                            <span>
                              {new Date(msg.created_at).toLocaleTimeString([], { 
                                hour: "2-digit", 
                                minute: "2-digit" 
                              })}
                            </span>
                            {msg.sender_id === user.id && (
                              <CheckCheck className={`w-3 h-3 ${msg.is_read ? "" : "opacity-50"}`} />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </ScrollArea>

                <form onSubmit={handleSendMessage} className="p-4 border-t border-olive/10">
                  <div className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="btn-primary px-4"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-6">
                <div>
                  <div className="w-16 h-16 rounded-full bg-olive/10 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-olive" />
                  </div>
                  <h3 className="font-heading text-xl text-charcoal mb-2">
                    Select a Conversation
                  </h3>
                  <p className="text-stone text-sm">
                    Choose a conversation from the list to view messages.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
